import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { adminAPI } from '../../../services/api';

// Maps each portfolio code to a master SAA (blender_code) and a branded client.
// Codes come from Shadow_Data (name + platform read live) UNION mapping rows for
// new portfolios that haven't traded yet — those carry a declared, editable platform
// so trade-sheet routing / unit-class mapping / platform compliance work from day one.
const PortfolioCodeMappingEditor = () => {
  const [codes, setCodes] = useState([]);     // [{portfolio_code, portfolio_name, platform, in_shadow, blender_code, client_domain}]
  const [masters, setMasters] = useState([]); // [{blender_code, name}]
  const [clients, setClients] = useState([]); // [{domain, company_name}]
  const [platformOptions, setPlatformOptions] = useState([]);
  const [grid, setGrid] = useState({});       // portfolio_code -> {blender_code, client_domain, platform}
  const [original, setOriginal] = useState({});
  const [selected, setSelected] = useState(() => new Set());   // rows a dropdown change fans out to

  const [search, setSearch] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  const [newRow, setNewRow] = useState({ portfolio_code: '', portfolio_name: '', platform: '', blender_code: '', client_domain: '' });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getPortfolioCodeMapping();
      const rows = data.codes || [];
      setCodes(rows);
      setMasters(data.masters || []);
      setClients(data.clients || []);
      setPlatformOptions(data.platform_options || []);
      const g = {};
      rows.forEach((r) => {
        g[r.portfolio_code] = {
          blender_code: r.blender_code || '', client_domain: r.client_domain || '',
          platform: r.platform || '',
          // Declared name is editable only for pre-Shadow rows; Shadow rows carry the
          // live name here (never rendered editable, so it never goes dirty).
          portfolio_name: r.portfolio_name || '',
          is_active: r.is_active !== false,
        };
      });
      setGrid(g);
      setOriginal(JSON.parse(JSON.stringify(g)));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load portfolio mapping');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Change a field on one row AND every checked row (bulk edit). Platform only applies to
  // pre-Shadow rows (registered rows read their platform live from Shadow_Data).
  const applyCell = (pc, field, value) => {
    setGrid((prev) => {
      const next = { ...prev };
      // Declared name is per-portfolio and pre-Shadow only — never fan out to ticked rows.
      const targets = field === 'portfolio_name' ? new Set([pc]) : new Set([pc, ...selected]);
      targets.forEach((code) => {
        if (field === 'platform' || field === 'portfolio_name') {
          const row = codes.find((x) => x.portfolio_code === code);
          if (!row || row.in_shadow !== false) return;
        }
        next[code] = { ...(next[code] || {}), [field]: value };
      });
      return next;
    });
  };

  const toggleSelect = (pc) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(pc)) n.delete(pc); else n.add(pc);
    return n;
  });

  const platforms = useMemo(
    () => [...new Set(codes.map((c) => c.platform).filter(Boolean))].sort(),
    [codes],
  );

  // Master SAAs grouped by portfolio suite for the dropdowns (suite-less masters last).
  const mastersBySuite = useMemo(() => {
    const groups = new Map();
    masters.forEach((m) => {
      const suite = m.suite || 'Other';
      if (!groups.has(suite)) groups.set(suite, []);
      groups.get(suite).push(m);
    });
    groups.forEach((list) => list.sort((a, b) =>
      a.blender_code.localeCompare(b.blender_code, undefined, { numeric: true })));
    return [...groups.entries()].sort(([a], [b]) => {
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a.localeCompare(b);
    });
  }, [masters]);

  const masterLabel = (m) =>
    `${m.blender_code}${m.name && m.name !== m.blender_code ? ` — ${m.name}` : ''}`;

  const masterOptions = mastersBySuite.map(([suite, list]) => (
    <optgroup key={suite} label={suite}>
      {list.map((m) => (
        <option key={m.blender_code} value={m.blender_code}>{masterLabel(m)}</option>
      ))}
    </optgroup>
  ));

  const clientName = useMemo(() => {
    const m = {};
    clients.forEach((c) => { m[c.domain] = c.company_name || c.domain; });
    return m;
  }, [clients]);

  const dirty = useMemo(() => {
    const changed = {};
    Object.keys(grid).forEach((pc) => {
      const g = grid[pc] || {};
      const o = original[pc] || {};
      const nameChanged = (g.portfolio_name || '') !== (o.portfolio_name || '');
      if ((g.blender_code || '') !== (o.blender_code || '') || (g.client_domain || '') !== (o.client_domain || '')
          || (g.platform || '') !== (o.platform || '')
          || (g.is_active !== false) !== (o.is_active !== false)
          || nameChanged) {
        changed[pc] = { blender_code: g.blender_code || null, client_domain: g.client_domain || null,
          platform: g.platform || null, is_active: g.is_active !== false };
        // Only send the declared name when it actually changed (pre-Shadow rows),
        // so a Shadow row edited for other fields never overwrites declared_name.
        if (nameChanged) changed[pc].portfolio_name = g.portfolio_name || null;
      }
    });
    return changed;
  }, [grid, original]);
  const dirtyCount = Object.keys(dirty).length;

  const addNewCode = async () => {
    const pc = newRow.portfolio_code.trim().toUpperCase();
    if (!pc) return;
    if (codes.some((c) => c.portfolio_code.toUpperCase() === pc)) {
      setError(`${pc} already exists in the list`);
      return;
    }
    setSaving(true); setError(null); setSuccess(null);
    try {
      await adminAPI.savePortfolioCodeMapping({ rows: { [pc]: {
        blender_code: newRow.blender_code || null,
        client_domain: newRow.client_domain || null,
        platform: newRow.platform || null,
        portfolio_name: newRow.portfolio_name.trim() || null,
      } } });
      setNewRow({ portfolio_code: '', portfolio_name: '', platform: '', blender_code: '', client_domain: '' });
      await fetchAll();
      setSuccess(`Added ${pc} — it will pick up its live name/platform once Shadow_Data appears`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add code');
    } finally {
      setSaving(false);
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return codes.filter((c) => {
      if (platformFilter && c.platform !== platformFilter) return false;
      if (unmappedOnly && (grid[c.portfolio_code]?.blender_code || '')) return false;
      if (q) {
        const hay = `${c.portfolio_code} ${c.portfolio_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [codes, search, platformFilter, unmappedOnly, grid]);

  const mappedCount = useMemo(
    () => codes.filter((c) => (grid[c.portfolio_code]?.blender_code || '')).length,
    [codes, grid],
  );

  const deadCount = useMemo(
    () => codes.filter((c) => grid[c.portfolio_code]?.is_active === false).length,
    [codes, grid],
  );

  const handleSave = async () => {
    if (dirtyCount === 0) return;
    setSaving(true); setError(null); setSuccess(null);
    try {
      await adminAPI.savePortfolioCodeMapping({ rows: dirty });
      await fetchAll();
      setSuccess(`Saved ${dirtyCount} mapping${dirtyCount === 1 ? '' : 's'}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4 text-muted">Loading portfolio mapping…</div>;

  return (
    <div className="portfolio-code-mapping">
      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
        <strong style={{ color: 'var(--primary-color)' }}>Portfolio Mapping</strong>
        <span className="text-muted small">
          Map each code to a master SAA and a branded client. Shadow codes read name &amp; platform live;
          new codes added here declare their platform until Shadow_Data appears.
        </span>
      </div>

      {error && <div className="alert alert-danger alert-dismissible">{error}<button className="btn-close" onClick={() => setError(null)} /></div>}
      {success && <div className="alert alert-success alert-dismissible">{success}<button className="btn-close" onClick={() => setSuccess(null)} /></div>}

      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <input
          className="form-control form-control-sm" style={{ width: 240 }}
          placeholder="Search code or name…" value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="form-select form-select-sm" style={{ width: 180 }}
          value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
          <option value="">All platforms</option>
          {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="form-check ms-1">
          <input className="form-check-input" type="checkbox" id="unmappedOnly"
            checked={unmappedOnly} onChange={(e) => setUnmappedOnly(e.target.checked)} />
          <label className="form-check-label small" htmlFor="unmappedOnly">Unmapped only</label>
        </div>
        {selected.size > 0 && (
          <span className="badge text-bg-info ms-auto" title="Changing any dropdown will apply to all ticked rows">
            {selected.size} ticked — dropdown changes apply to all
            <button className="btn btn-sm btn-link text-white p-0 ms-2" style={{ textDecoration: 'underline' }}
              onClick={() => setSelected(new Set())}>clear</button>
          </span>
        )}
        <span className={`text-muted small ${selected.size > 0 ? '' : 'ms-auto'}`}>
          {mappedCount}/{codes.length} mapped{deadCount > 0 ? ` · ${deadCount} dead` : ''} · showing {visible.length}
        </span>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || dirtyCount === 0}>
          {saving ? 'Saving…' : dirtyCount > 0 ? `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}` : 'Save'}
        </button>
      </div>

      <div className="d-flex align-items-center flex-wrap gap-2 mb-3 p-2"
        style={{ background: '#f8f9fa', borderRadius: 6, border: '1px solid #dee2e6' }}>
        <span className="small fw-semibold" style={{ color: 'var(--primary-color)' }}
          title="Create a mapping row for a portfolio that doesn't have Shadow_Data yet — this puts it in the trading universe (cascade, trade sheets, compliance) from day one">
          New portfolio
        </span>
        <input className="form-control form-control-sm" style={{ width: 160, fontFamily: 'monospace' }}
          placeholder="Portfolio code…" value={newRow.portfolio_code}
          onChange={(e) => setNewRow((r) => ({ ...r, portfolio_code: e.target.value.toUpperCase() }))} />
        <input className="form-control form-control-sm" style={{ width: 200 }}
          title="Declared name — shown until Shadow_Data provides the live name, then that overrides it"
          placeholder="Portfolio name (optional)…" value={newRow.portfolio_name}
          onChange={(e) => setNewRow((r) => ({ ...r, portfolio_name: e.target.value }))} />
        <select className="form-select form-select-sm" style={{ width: 170 }}
          value={newRow.platform} onChange={(e) => setNewRow((r) => ({ ...r, platform: e.target.value }))}>
          <option value="">Platform…</option>
          {platformOptions.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="form-select form-select-sm" style={{ width: 220 }}
          value={newRow.blender_code} onChange={(e) => setNewRow((r) => ({ ...r, blender_code: e.target.value }))}>
          <option value="">Master SAA…</option>
          {masterOptions}
        </select>
        <select className="form-select form-select-sm" style={{ width: 200 }}
          value={newRow.client_domain} onChange={(e) => setNewRow((r) => ({ ...r, client_domain: e.target.value }))}>
          <option value="">Client (optional)…</option>
          {clients.map((cl) => <option key={cl.domain} value={cl.domain}>{cl.company_name || cl.domain}</option>)}
        </select>
        <button className="btn btn-outline-primary btn-sm" onClick={addNewCode}
          disabled={saving || !newRow.portfolio_code.trim() || !newRow.platform || !newRow.blender_code}>
          + Add
        </button>
      </div>

      <div className="table-responsive" style={{ maxHeight: '64vh' }}>
        <table className="table table-sm table-hover align-middle" style={{ fontSize: '0.8rem' }}>
          <thead style={{ position: 'sticky', top: 0, background: 'var(--primary-color)', color: '#fff', zIndex: 1 }}>
            <tr>
              <th style={{ width: 34 }} title="Tick rows to bulk-apply a dropdown change to all of them">
                <input type="checkbox"
                  checked={visible.length > 0 && visible.every((c) => selected.has(c.portfolio_code))}
                  ref={(el) => { if (el) el.indeterminate = visible.some((c) => selected.has(c.portfolio_code)) && !visible.every((c) => selected.has(c.portfolio_code)); }}
                  onChange={() => setSelected((prev) => {
                    const n = new Set(prev);
                    const all = visible.every((c) => n.has(c.portfolio_code));
                    visible.forEach((c) => { if (all) n.delete(c.portfolio_code); else n.add(c.portfolio_code); });
                    return n;
                  })} />
              </th>
              <th>Code</th>
              <th>Name</th>
              <th style={{ width: 90 }}
                title="Dead portfolios stay here for the record but are hidden from every other portfolio list in the portal">
                Status
              </th>
              <th>Platform</th>
              <th style={{ minWidth: 220 }}>Master SAA</th>
              <th style={{ minWidth: 200 }}>Client</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => {
              const g = grid[c.portfolio_code] || {};
              const isDirty = !!dirty[c.portfolio_code];
              const isDead = g.is_active === false;
              return (
                <tr key={c.portfolio_code}
                  style={{
                    ...(isDirty ? { background: '#fff8e6' }
                      : selected.has(c.portfolio_code) ? { background: '#eef4fb' } : {}),
                    ...(isDead ? { opacity: 0.55 } : {}),
                  }}>
                  <td>
                    <input type="checkbox" checked={selected.has(c.portfolio_code)}
                      onChange={() => toggleSelect(c.portfolio_code)} />
                  </td>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.portfolio_code}</td>
                  <td>
                    {c.in_shadow === false ? (
                      <input className="form-control form-control-sm" style={{ minWidth: 180 }}
                        title="Declared name — shown until Shadow_Data provides the live name, then that overrides it"
                        placeholder="Declared name…"
                        value={g.portfolio_name || ''}
                        onChange={(e) => applyCell(c.portfolio_code, 'portfolio_name', e.target.value)} />
                    ) : (c.portfolio_name || '—')}
                  </td>
                  <td>
                    <div className="form-check form-switch m-0"
                      title={isDead
                        ? 'Dead — hidden from every other portfolio list in the portal'
                        : 'Active — visible across the portal'}>
                      <input className="form-check-input" type="checkbox" role="switch"
                        checked={!isDead}
                        onChange={(e) => applyCell(c.portfolio_code, 'is_active', e.target.checked)} />
                      <label className="form-check-label small">
                        {isDead ? <span className="text-danger fw-semibold">Dead</span> : 'Active'}
                      </label>
                    </div>
                  </td>
                  <td className="text-muted">
                    {c.in_shadow === false ? (
                      <select className="form-select form-select-sm" style={{ minWidth: 140 }}
                        title="Declared platform — used for trade-sheet routing, unit-class mapping and platform compliance until Shadow_Data appears"
                        value={g.platform || ''}
                        onChange={(e) => applyCell(c.portfolio_code, 'platform', e.target.value)}>
                        <option value="">— None —</option>
                        {platformOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    ) : (c.platform || '—')}
                  </td>
                  <td>
                    <select className="form-select form-select-sm"
                      value={g.blender_code || ''}
                      onChange={(e) => applyCell(c.portfolio_code, 'blender_code', e.target.value)}>
                      <option value="">— Unmapped —</option>
                      {masterOptions}
                      {g.blender_code && !masters.some((m) => m.blender_code === g.blender_code) && (
                        <optgroup label="Not in registry">
                          <option value={g.blender_code}>{g.blender_code} (not a registry master)</option>
                        </optgroup>
                      )}
                    </select>
                  </td>
                  <td>
                    <select className="form-select form-select-sm"
                      value={g.client_domain || ''}
                      onChange={(e) => applyCell(c.portfolio_code, 'client_domain', e.target.value)}>
                      <option value="">— None —</option>
                      {clients.map((cl) => (
                        <option key={cl.domain} value={cl.domain}>{cl.company_name || cl.domain}</option>
                      ))}
                      {g.client_domain && !clients.some((cl) => cl.domain === g.client_domain) && (
                        <option value={g.client_domain}>{clientName[g.client_domain] || g.client_domain} (missing)</option>
                      )}
                    </select>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted py-4">No codes match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PortfolioCodeMappingEditor;
